import axios, { AxiosError } from 'axios';

const TOKEN_KEY = 'auth_token';
const NAME_KEY = 'user_name';
const EMAIL_KEY = 'user_email';
const SESSION_KEY = 'chat_session';
const RESET_KEY = 'password_reset_key';
const PENDING_JOB_CIPHER_KEY = 'pending_job_cipher';

export const authUtils = {
  setToken: (token: string) => {
    localStorage.setItem(TOKEN_KEY, token);
  },

  getToken: (): string | null => {
    return localStorage.getItem(TOKEN_KEY);
  },

  removeToken: () => {
    localStorage.removeItem(TOKEN_KEY);
  },

  setUserName: (name: string) => {
    localStorage.setItem(NAME_KEY, name);
  },

  getUserName: (): string | null => {
    return localStorage.getItem(NAME_KEY);
  },

  removeUserName: () => {
    localStorage.removeItem(NAME_KEY);
  },

  setEmail: (email: string) => {
    localStorage.setItem(EMAIL_KEY, email);
  },

  getEmail: (): string | null => {
    return localStorage.getItem(EMAIL_KEY);
  },

  removeEmail: () => {
    localStorage.removeItem(EMAIL_KEY);
  },

  setSession: (sessionId: string) => {
    localStorage.setItem(SESSION_KEY, sessionId);
  },

  getSession: (): string | null => {
    return localStorage.getItem(SESSION_KEY);
  },

  removeSession: () => {
    localStorage.removeItem(SESSION_KEY);
  },

  setResetKey: (key: string) => {
    localStorage.setItem(RESET_KEY, key);
  },

  getResetKey: (): string | null => {
    return localStorage.getItem(RESET_KEY);
  },

  removeResetKey: () => {
    localStorage.removeItem(RESET_KEY);
  },

  clearAll: () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(NAME_KEY);
    localStorage.removeItem(EMAIL_KEY);
    localStorage.removeItem(SESSION_KEY);
    localStorage.removeItem(RESET_KEY);
  },

  isAuthenticated: (): boolean => {
    return !!localStorage.getItem(TOKEN_KEY);
  },

  generateSessionId: (): string => {
    return 'session_' + Date.now() + '_' + Math.random().toString(36).substring(2, 15);
  },

  setPendingJobCipher: (cipher: string) =>
    localStorage.setItem(PENDING_JOB_CIPHER_KEY, cipher),

  getPendingJobCipher: (): string | null =>
    localStorage.getItem(PENDING_JOB_CIPHER_KEY),

  removePendingJobCipher: () =>
    localStorage.removeItem(PENDING_JOB_CIPHER_KEY),
};

// Set default axios timeout for long-running chat requests
if (axios.defaults) {
  axios.defaults.timeout = 180000; // 3 minutes
}

// Axios interceptor to add token to headers
axios.interceptors.request.use(
  (config) => {
    const token = authUtils.getToken();
    
    // Add token for all requests except auth endpoints (login, register, etc.)
    // Note: verify-token endpoint should NOT have token in header (it's in the body)
    const isAuthEndpoint = config.url?.includes('/login') ||
                          config.url?.includes('/register') ||
                          config.url?.includes('/verify-email') ||
                          config.url?.includes('/forgot-password') ||
                          config.url?.includes('/verify-reset-code') ||
                          config.url?.includes('/change-password') ||
                          config.url?.includes('/verify-token') || // Token is sent in body for this endpoint
                          config.url?.includes('/api/auth'); // Include proxy auth URLs
    
    // Log axios interceptor activity for profile requests
    const isProfileRequest = config.url?.includes('/api/profile') || config.url?.includes('document-processor');
    if (isProfileRequest) {
      console.log('\n=== AXIOS REQUEST INTERCEPTOR ===');
      console.log('🌐 Request URL:', config.url);
      console.log('🔑 Token Available:', !!token);
      console.log('🔑 Token (first 30 chars):', token ? token.substring(0, 30) + '...' : 'None');
      console.log('🚫 Is Auth Endpoint:', isAuthEndpoint);
      console.log('📄 Current Headers:', Object.keys(config.headers || {}));
    }
    
    if (token && !isAuthEndpoint) {
      // Profile API expects just the token value, not "Bearer <token>"
      // Check both development (/api/profile) and production (document-processor) URLs
      const isProfileAPI = config.url?.includes('/api/profile') || 
                          config.url?.includes('document-processor');
      
      if (isProfileAPI) {
        config.headers.Authorization = token;
        console.log('✅ Authorization header added (token only for profile API)');
      } else {
        // Other APIs expect "Bearer <token>" format
        config.headers.Authorization = `Bearer ${token}`;
        console.log('✅ Authorization header added (Bearer format for other APIs)');
      }
    } else if (config.url?.includes('/api/profile') || config.url?.includes('document-processor')) {
      console.log('❌ Authorization header NOT added - Token:', !!token, 'AuthEndpoint:', isAuthEndpoint);
    }
    
    // Set timeouts based on endpoint type
    if (config.url?.includes('/api/chat')) {
      config.timeout = 180000; // 3 minutes for chat
    } else if (isAuthEndpoint) {
      config.timeout = 30000; // 30 seconds for auth
    }
    
    if (isProfileRequest) {
      console.log('=== AXIOS REQUEST INTERCEPTOR END ===\n');
    }
    
    return config;
  },
  (error) => {
    console.error('❌ Axios Request Interceptor Error:', error);
    return Promise.reject(error);
  }
);

// Axios interceptor to handle authentication errors
axios.interceptors.response.use(
  (response) => {
    // Log successful responses for profile requests
    const isProfileResponse = response.config.url?.includes('/api/profile') || response.config.url?.includes('document-processor');
    if (isProfileResponse) {
      console.log('\n=== AXIOS RESPONSE INTERCEPTOR (SUCCESS) ===');
      console.log('✅ Response Status:', response.status);
      console.log('🌐 Request URL:', response.config.url);
      console.log('=== AXIOS RESPONSE INTERCEPTOR END ===\n');
    }
    return response;
  },
  (error: AxiosError) => {
    // Log error responses for profile requests
    const isProfileError = error.config?.url?.includes('/api/profile') || error.config?.url?.includes('document-processor');
    if (isProfileError) {
      console.log('\n=== AXIOS RESPONSE INTERCEPTOR (ERROR) ===');
      console.log('❌ Error Status:', error.response?.status);
      console.log('🌐 Request URL:', error.config?.url);
      console.log('📦 Error Data:', error.response?.data);
      console.log('🔍 Error Message:', error.message);
    }
    
    // Check if it's a quota error (403 with quota-related message)
    const errorData = error.response?.data as any;
    const isQuotaError = error.response?.status === 403 &&
                        errorData?.error &&
                        (errorData.error.includes('quota') ||
                         errorData.error.includes('subscription'));

    // Only log out for authentication errors (401) or non-quota 403 errors
    if ((error.response?.status === 401 || error.response?.status === 403) && !isQuotaError) {
      console.log('🔐 Authentication error detected in response interceptor');
      console.log('🧹 Clearing auth data and redirecting to login');
      console.log('=== AXIOS RESPONSE INTERCEPTOR (REDIRECT) ===\n');

      authUtils.clearAll();
      window.location.href = '/login';
    } else if (isQuotaError) {
      console.log('⚠️ Quota error detected - NOT logging out user');
      console.log('=== AXIOS RESPONSE INTERCEPTOR (QUOTA ERROR) ===\n');
    } else if (isProfileError) {
      console.log('=== AXIOS RESPONSE INTERCEPTOR END ===\n');
    }
    
    return Promise.reject(error);
  }
);